import { useReducer, useState, useRef, useCallback, useEffect } from 'react'
import { MapRef } from 'react-map-gl/mapbox'
import { Dayjs } from 'dayjs'
import { DragEndEvent } from '@dnd-kit/core'
import mapboxgl from 'mapbox-gl'
import ReorderDayViewTitleBar from './ReorderDayViewTitleBar'
import ReorderDayViewEventList from './ReorderDayViewEventList'
import ReorderDayViewMap from './ReorderDayViewMap'
import { useSchedule } from '../../ScheduleContext'
import { useScheduledEventsByDateQuery, useBulkUpdateScheduledEventTimeMutation } from '../../scheduleQueries'
import { useTenantLocationsDataQuery } from '../../../../domain/tenantLocations/tenantLocationsQueries'
import { TenantLocationType } from '../../../../domain/tenantLocations/tenantLocation'
import { defaultReorderDayViewState, reducer, dragReorderOrderChanged, createEventLocation, isMultiDayEvent, sliceEventsForOptimization, recombineOptimizedEvents } from './reorderDayViewModel'
import { getDrivingDirections } from '../../../RouteManagement/Mappers/mapbox'
import { Spin, notification } from 'antd'
import { ScheduledEvent } from '../../scheduleApi'
import dayjs from 'dayjs'
import { eventHasVisit, getDayDateRange, UNASSIGNED_CREW_ID } from '../../scheduleModel'
import { emailsMatch } from '../../crewModel'

type Props = {
  selectedCrewLeadId: number | typeof UNASSIGNED_CREW_ID | null
  date: Dayjs
  onCancel: () => void
  onSave: () => void
}

const getEventDurationMinutes = (event: ScheduledEvent): number => {
  const start = dayjs(event.start)
  const end = dayjs(event.end)
  const durationMinutes = end.diff(start, 'minutes')
  return durationMinutes > 0 ? durationMinutes : 60
}

const ReorderDayView = ({ selectedCrewLeadId, date, onCancel, onSave }: Props) => {
  const { crew } = useSchedule()
  const scheduledEventsDayViewQuery = useScheduledEventsByDateQuery(date.format('YYYY-MM-DD'))
  const { data: tenantLocations } = useTenantLocationsDataQuery()
  const bulkUpdateScheduledEventTimeMutation = useBulkUpdateScheduledEventTimeMutation()

  const [state, dispatch] = useReducer(reducer, defaultReorderDayViewState)
  const [totalTime, setTotalTime] = useState(0)
  const [distance, setDistance] = useState(0)
  const [optimizeRange, setOptimizeRange] = useState<[number, number]>([1, 23])
  const [hoveredEventId, setHoveredEventId] = useState<number | null>(null)
  const [isMapLoaded, setIsMapLoaded] = useState(false)
  const [mapStyle, setMapStyle] = useState("mapbox://styles/mapbox/satellite-v9")
  const [isStyleChanging, setIsStyleChanging] = useState(false)
  const [currentRouteData, setCurrentRouteData] = useState<any>(null)
  const [isSaving, setIsSaving] = useState(false)

  const mapRef = useRef<MapRef>(null)

  const crewLead = selectedCrewLeadId === UNASSIGNED_CREW_ID
    ? { name: 'Unassigned Events', avatar: undefined, hasStockAvatar: true }
    : crew?.find(c => c.id === selectedCrewLeadId)

  const crewLeadName = crewLead?.name || 'Unknown'
  const crewLeadAvatar = crewLead?.hasStockAvatar ? undefined : crewLead?.avatar

  const events = scheduledEventsDayViewQuery.data?.scheduledEvents ?? []
  const visits = scheduledEventsDayViewQuery.data?.visits ?? []
  const calendarEventVisits = scheduledEventsDayViewQuery.data?.calendarEventVisits ?? []
  const shopLocation = tenantLocations?.find(tl => tl.type === TenantLocationType.Shop)

  const crewLeadEmail = selectedCrewLeadId === UNASSIGNED_CREW_ID
    ? null
    : crew?.find(c => c.id === selectedCrewLeadId)?.userEmail

  const eventsForSelectedCrewLead = events.filter(event => {
    if (selectedCrewLeadId === UNASSIGNED_CREW_ID) {
      return !event.crewForemanEmail || event.crewForemanEmail.trim() === ''
    }

    return emailsMatch(event.crewForemanEmail, crewLeadEmail)
  })

  const filteredEvents = eventsForSelectedCrewLead.filter(event => {
    if (event.allDay) return false
    if (isMultiDayEvent(event)) return false
    if (eventHasVisit(event, calendarEventVisits, visits, getDayDateRange(date))) return false

    return true
  })

  const visitedEventsForCrewLead = events.filter(event => {
    if (event.allDay) return false
    if (isMultiDayEvent(event)) return false
    if (!eventHasVisit(event, calendarEventVisits, visits, getDayDateRange(date))) return false

    if (selectedCrewLeadId === UNASSIGNED_CREW_ID) {
      return !event.crewForemanEmail || event.crewForemanEmail.trim() === ''
    }

    return emailsMatch(event.crewForemanEmail, crewLeadEmail)
  })

  const lastVisitedEvent = visitedEventsForCrewLead.length > 0
    ? visitedEventsForCrewLead.sort((a, b) => dayjs(b.end).valueOf() - dayjs(a.end).valueOf())[0]
    : null

  const lastVisitedEventLocation = lastVisitedEvent ? createEventLocation(lastVisitedEvent) : null

  const locations = [
    ...(shopLocation ? [{
      id: `shop-${shopLocation.id}`,
      jobName: 'My Shop',
      latitude: shopLocation.latitude,
      longitude: shopLocation.longitude,
      address1: shopLocation.address1,
      address2: shopLocation.address2,
      city: shopLocation.city,
      stateCode: shopLocation.stateCode,
      zip: shopLocation.zip
    }] : []),
    ...(lastVisitedEventLocation ? [lastVisitedEventLocation] : []),
    ...filteredEvents.map(createEventLocation)
  ]

  if (state.events.length === 0 && filteredEvents.length > 0) {
    dispatch({ type: 'EVENTS_LOADED', value: filteredEvents })
    dispatch({ type: 'START_TIME_CHANGED', value: date.hour(8).minute(0).second(0).toISOString(), isInitialLoad: true })
    if (lastVisitedEventLocation) {
      dispatch({ type: 'STARTING_LOCATION_CHANGED', value: lastVisitedEventLocation, isInitialLoad: true })
      const shopLoc = shopLocation ? locations[0] : undefined
      dispatch({ type: 'ENDING_LOCATION_CHANGED', value: shopLoc, isInitialLoad: true })
    } else if (shopLocation) {
      const shopLoc = locations[0]
      dispatch({ type: 'STARTING_LOCATION_CHANGED', value: shopLoc, isInitialLoad: true })
      dispatch({ type: 'ENDING_LOCATION_CHANGED', value: shopLoc, isInitialLoad: true })
    }
  }

  const createCoords = useCallback(() => {
    const coords: number[][] = []

    if (state.events.length === 0) return coords

    if (state.startingLocation?.latitude && state.startingLocation?.longitude) {
      coords.push([state.startingLocation.longitude, state.startingLocation.latitude])
    }

    state.events.forEach(event => {
      if (event.latitude && event.longitude) {
        if (state.startingLocation &&
          event.latitude === state.startingLocation.latitude &&
          event.longitude === state.startingLocation.longitude) {
          return
        }
        if (state.endingLocation &&
          event.latitude === state.endingLocation.latitude &&
          event.longitude === state.endingLocation.longitude) {
          return
        }
        coords.push([event.longitude, event.latitude])
      }
    })

    if (state.endingLocation?.latitude && state.endingLocation?.longitude) {
      coords.push([state.endingLocation.longitude, state.endingLocation.latitude])
    }

    return coords
  }, [state.events, state.startingLocation, state.endingLocation])

  const displayDrivingDirections = useCallback(async () => {
    const coords = createCoords()

    if (coords.length === 0) return

    try {
      const data = await getDrivingDirections(coords)
      if (data && data.routes && data.routes.length > 0) {
        setTotalTime(data.routes[0].duration)
        setDistance(data.routes[0].distance)
        setCurrentRouteData(data)
        displayRouteOnMap(data, true)
      }
    } catch (error) {
      console.error('Error fetching driving directions:', error)
    }
  }, [createCoords])

  const displayRouteOnMap = useCallback((routeData: any, shouldFitBounds: boolean) => {
    if (!mapRef.current || !routeData || !routeData.routes || !routeData.routes[0]) return

    const map = mapRef.current.getMap()
    if (!map) return

    const attemptAddRoute = () => {
      try {
        if (!map.getSource('route')) {
          const trip = routeData.routes[0]
          const routeGeoJSON = {
            type: 'Feature' as const,
            properties: {},
            geometry: {
              type: 'LineString' as const,
              coordinates: trip.geometry.coordinates
            }
          }

          map.addSource('route', {
            type: 'geojson',
            data: routeGeoJSON
          })

          map.addLayer({
            id: 'route',
            type: 'line',
            source: 'route',
            layout: {
              'line-join': 'round',
              'line-cap': 'round'
            },
            paint: {
              'line-color': '#FF5C35',
              'line-width': 4,
              'line-opacity': 0.8
            }
          })

          map.addLayer({
            id: 'routearrows',
            type: 'symbol',
            source: 'route',
            layout: {
              'symbol-placement': 'line',
              'text-field': '▶',
              'text-size': ['interpolate', ['linear'], ['zoom'], 12, 24, 22, 60],
              'symbol-spacing': ['interpolate', ['linear'], ['zoom'], 12, 30, 22, 160],
              'text-keep-upright': false
            },
            paint: {
              'text-color': '#FF5C35',
              'text-halo-color': 'hsl(55, 11%, 96%)',
              'text-halo-width': 3
            }
          })

          if (shouldFitBounds) {
            const bounds = new mapboxgl.LngLatBounds()
            trip.geometry.coordinates.forEach((coord: [number, number]) => {
              bounds.extend(coord)
            })
            map.fitBounds(bounds, { padding: 50 })
          }

          return true
        } else {
          const trip = routeData.routes[0]
          const routeGeoJSON = {
            type: 'Feature' as const,
            properties: {},
            geometry: {
              type: 'LineString' as const,
              coordinates: trip.geometry.coordinates
            }
          }

          const source = map.getSource('route') as mapboxgl.GeoJSONSource
          if (source) {
            source.setData(routeGeoJSON)
          }

          if (shouldFitBounds) {
            const bounds = new mapboxgl.LngLatBounds()
            trip.geometry.coordinates.forEach((coord: [number, number]) => {
              bounds.extend(coord)
            })
            map.fitBounds(bounds, { padding: 50 })
          }

          return true
        }
      } catch (error) {
        return false
      }
    }

    if (attemptAddRoute()) {
      return
    }

    const retryAttempts = [200, 500, 1000, 1500, 2000]
    let currentAttempt = 0

    const retryAddRoute = () => {
      if (currentAttempt >= retryAttempts.length) {
        return
      }

      setTimeout(() => {
        if (attemptAddRoute()) {
          return
        }

        currentAttempt++
        retryAddRoute()
      }, retryAttempts[currentAttempt])
    }

    retryAddRoute()
  }, [])

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event
    if (!over || active.id === over.id) return

    const reorderedEvents = dragReorderOrderChanged(active.id, over.id, state)
    dispatch({ type: 'EVENTS_LOADED', value: reorderedEvents, setHasChanges: true })
  }

  useEffect(() => {
    if (state.events.length > 0) {
      setOptimizeRange([1, Math.min(state.events.length, 23)])
    }
  }, [state.events.length])

  useEffect(() => {
    if (isMapLoaded && state.events.length > 0 && state.startingLocation && state.endingLocation) {
      displayDrivingDirections()
    }
  }, [isMapLoaded, state.events, state.startingLocation, state.endingLocation, displayDrivingDirections])

  const handleStartTimeChange = (time: string) => {
    dispatch({ type: 'START_TIME_CHANGED', value: time })
  }

  const handleStartingLocationChange = (value: string) => {
    const location = locations.find(loc => loc.id === value)
    dispatch({ type: 'STARTING_LOCATION_CHANGED', value: location })
  }

  const handleEndingLocationChange = (value: string) => {
    const location = locations.find(loc => loc.id === value)
    dispatch({ type: 'ENDING_LOCATION_CHANGED', value: location })
  }

  const handleShopAddressUpdate = (coordinates: { latitude: number; longitude: number }) => {
    dispatch({
      type: 'SHOP_COORDINATES_UPDATED',
      latitude: coordinates.latitude,
      longitude: coordinates.longitude
    })
  }

  const handleEventLocationUpdate = (eventId: number, latitude: number, longitude: number) => {
    dispatch({ type: 'EVENT_LOCATION_UPDATED', eventId, latitude, longitude })
  }

  const handleSave = async () => {
    setIsSaving(true)

    try {
      let currentStartTime = dayjs(state.startTime)
      const eventUpdates = []

      for (const event of state.events) {
        const durationMinutes = getEventDurationMinutes(event)
        const newStartTime = currentStartTime
        const newEndTime = currentStartTime.add(durationMinutes, 'minutes')

        eventUpdates.push({
          eventId: event.id,
          start: newStartTime.toISOString(),
          end: newEndTime.toISOString()
        })

        currentStartTime = newEndTime
      }

      await bulkUpdateScheduledEventTimeMutation.mutateAsync({ eventUpdates })

      onSave()
    } catch (error) {
      console.error('Save error:', error)
      notification.error({
        message: 'Save Failed',
        description: 'Failed to update the schedule. Please try again.'
      })
    } finally {
      setIsSaving(false)
    }
  }

  const handleOptimizeDay = async () => {
    if (!state.startingLocation || !state.endingLocation) {
      return
    }

    try {
      const { eventsBefore, eventsInRange, eventsAfter } = sliceEventsForOptimization(state.events, optimizeRange)

      const googleMapsRequest = {
        origin: {
          location: {
            latLng: {
              latitude: state.startingLocation.latitude,
              longitude: state.startingLocation.longitude
            }
          }
        },
        destination: {
          location: {
            latLng: {
              latitude: state.endingLocation.latitude,
              longitude: state.endingLocation.longitude
            }
          }
        },
        intermediates: eventsInRange.map(event => ({
          location: {
            latLng: {
              latitude: event.latitude || 0,
              longitude: event.longitude || 0
            }
          }
        })),
        travelMode: 'DRIVE',
        optimizeWaypointOrder: true
      }

      const response = await fetch('https://routes.googleapis.com/directions/v2:computeRoutes', {
        method: 'POST',
        body: JSON.stringify(googleMapsRequest),
        headers: {
          'Content-Type': 'application/json',
          'X-Goog-FieldMask': 'routes,geocodingResults.intermediates.intermediateWaypointRequestIndex',
          'X-Goog-Api-Key': (window as any).env.GOOGLE_API_KEY
        }
      })

      const googleData = await response.json()

      if (!googleData || !googleData.routes || !googleData.routes[0] || !googleData.routes[0].optimizedIntermediateWaypointIndex) {
        alert('Route optimization failed. Please try again or check your network connection.')
        return
      }

      const optimizedEventsInRange = googleData.routes[0].optimizedIntermediateWaypointIndex.map((originalIndex: number) => {
        return eventsInRange[originalIndex]
      })

      const allEvents = recombineOptimizedEvents(eventsBefore, optimizedEventsInRange, eventsAfter)

      dispatch({ type: 'EVENTS_LOADED', value: allEvents, setHasChanges: true })
    } catch (error) {
      console.error('Optimization error:', error)
      alert('Route optimization failed. Please try again or check your network connection.')
    }
  }

  if (scheduledEventsDayViewQuery.isLoading) {
    return (
      <>
        <ReorderDayViewTitleBar
          crewLeadName={crewLeadName}
          crewLeadAvatar={crewLeadAvatar}
          onCancel={onCancel}
          onSave={handleSave}
          hasChanges={state.hasChanges}
          isSaving={isSaving}
        />
        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '66vh' }}>
          <Spin size="large" />
        </div>
      </>
    )
  }

  return (
    <>
      <ReorderDayViewTitleBar
        crewLeadName={crewLeadName}
        crewLeadAvatar={crewLeadAvatar}
        onCancel={onCancel}
        onSave={handleSave}
        hasChanges={state.hasChanges}
        isSaving={isSaving}
      />

      <div style={{ display: 'flex', flex: 1, minHeight: '66vh', maxHeight: '66vh', overflow: 'hidden' }}>
        <ReorderDayViewEventList
          state={state}
          totalTime={totalTime}
          distance={distance}
          locations={locations}
          hoveredEventId={hoveredEventId}
          setHoveredEventId={setHoveredEventId}
          onStartTimeChange={handleStartTimeChange}
          onStartingLocationChange={handleStartingLocationChange}
          onEndingLocationChange={handleEndingLocationChange}
          onDragEnd={handleDragEnd}
          onOptimizeDay={handleOptimizeDay}
          onEventLocationUpdate={handleEventLocationUpdate}
          optimizeRange={optimizeRange}
          onOptimizeRangeChange={setOptimizeRange}
          showFilterWarning={filteredEvents.length < eventsForSelectedCrewLead.length}
        />

        <ReorderDayViewMap
          state={state}
          mapRef={mapRef}
          hoveredEventId={hoveredEventId}
          setHoveredEventId={setHoveredEventId}
          setIsMapLoaded={setIsMapLoaded}
          mapStyle={mapStyle}
          setMapStyle={setMapStyle}
          isStyleChanging={isStyleChanging}
          setIsStyleChanging={setIsStyleChanging}
          currentRouteData={currentRouteData}
          displayRouteOnMap={displayRouteOnMap}
          displayDrivingDirections={displayDrivingDirections}
          shopLocation={shopLocation}
          tenantLocations={tenantLocations}
          onShopAddressUpdate={handleShopAddressUpdate}
        />
      </div>
    </>
  )
}

export default ReorderDayView